import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { API } from '../config';


const categoryOptions = [
  { value: 'utility', en: 'Utility', fr: 'Utilitaire' },
  { value: 'ocr', en: 'Text extraction (OCR)', fr: 'Extraction de texte (OCR)' },
  { value: 'converter', en: 'Converter', fr: 'Convertisseur' },
  { value: 'template', en: 'Templates', fr: 'Modèles' },
  { value: 'security', en: 'Security', fr: 'Sécurité' },
  { value: 'annotation', en: 'Annotation', fr: 'Annotation' },
  { value: 'integration', en: 'Integration', fr: 'Intégration' },
];

export default function SubmitModulePage() {
  const { lang } = useI18n();
  const { getToken, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', slug: '', description: '', short_description: '',
    version: '1.0.0', category: 'utility', price: 0, tags: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        slug: form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      await axios.post(`${API}/marketplace/submit`, payload, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(lang === 'fr' ? 'Module soumis pour validation !' : 'Module submitted for review!');
      navigate('/marketplace');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  return (
    <div className="min-h-screen bg-background" data-testid="submit-module-page">
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="max-w-xl mx-auto px-6 md:px-12">
          <button onClick={() => navigate('/marketplace')} className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-white text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />{lang === 'fr' ? 'Retour au marketplace' : 'Back to marketplace'}
          </button>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-heading text-3xl font-semibold text-foreground mb-2" data-testid="submit-module-title">
              {lang === 'fr' ? 'Soumettre un module' : 'Submit a Module'}
            </h1>
            <p className="text-muted-foreground text-sm mb-8">
              {lang === 'fr' ? 'Votre module sera examiné avant publication.' : 'Your module will be reviewed before publishing.'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="submit-module-form">
              <div>
                <Label className="text-muted-foreground text-sm mb-1.5 block">{lang === 'fr' ? 'Nom' : 'Name'} *</Label>
                <Input value={form.name} onChange={set('name')} required className="bg-card border-border text-foreground h-10 rounded-lg" data-testid="module-name-input" />
              </div>
              <div>
                <Label className="text-muted-foreground text-sm mb-1.5 block">Slug</Label>
                <Input value={form.slug} onChange={set('slug')} placeholder="auto-generated" className="bg-card border-border text-foreground h-10 rounded-lg" data-testid="module-slug-input" />
              </div>
              <div>
                <Label className="text-muted-foreground text-sm mb-1.5 block">{lang === 'fr' ? 'Description courte' : 'Short description'}</Label>
                <Input value={form.short_description} onChange={set('short_description')} className="bg-card border-border text-foreground h-10 rounded-lg" data-testid="module-short-desc-input" />
              </div>
              <div>
                <Label className="text-muted-foreground text-sm mb-1.5 block">Description *</Label>
                <textarea value={form.description} onChange={set('description')} required rows={5}
                  className="w-full bg-card border border-border text-foreground rounded-lg text-sm p-3 outline-none focus:border-blue-500 resize-y" data-testid="module-desc-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-muted-foreground text-sm mb-1.5 block">{lang === 'fr' ? 'Catégorie' : 'Category'}</Label>
                  <select value={form.category} onChange={set('category')}
                    className="w-full bg-card border border-border text-foreground h-10 rounded-lg text-sm px-3 outline-none" data-testid="module-category-select">
                    {categoryOptions.map(c => <option key={c.value} value={c.value}>{c[lang] || c.en}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm mb-1.5 block">Version</Label>
                  <Input value={form.version} onChange={set('version')} className="bg-card border-border text-foreground h-10 rounded-lg" data-testid="module-version-input" />
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm mb-1.5 block">{lang === 'fr' ? 'Prix (EUR)' : 'Price (EUR)'}</Label>
                  <Input type="number" min="0" step="0.01" value={form.price} onChange={set('price')} className="bg-card border-border text-foreground h-10 rounded-lg" data-testid="module-price-input" />
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm mb-1.5 block">Tags ({lang === 'fr' ? 'séparés par des virgules' : 'comma-separated'})</Label>
                <Input value={form.tags} onChange={set('tags')} className="bg-card border-border text-foreground h-10 rounded-lg" data-testid="module-tags-input" />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-full h-11 font-medium shadow-[0_0_15px_rgba(59,130,246,0.4)]"
                data-testid="module-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />{lang === 'fr' ? 'Soumettre' : 'Submit'}</>}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
